import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useNotice } from '../DialogProvider';
import styled from '@emotion/styled';
import { Download, FileJson, FileSpreadsheet, Loader2 } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  color: #ddd;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
  font-weight: 600;
`;

const FilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Pill = styled.span`
  padding: 3px 10px;
  background: rgba(74, 158, 255, 0.12);
  border: 1px solid rgba(74, 158, 255, 0.3);
  border-radius: 999px;
  font-size: 12px;
  color: #8ab4f8;
  font-family: ${p => p.$mono ? 'monospace' : 'inherit'};
`;

const PillMuted = styled(Pill)`
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
  color: #888;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RadioRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: ${p => p.$selected ? 'rgba(74, 158, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${p => p.$selected ? 'rgba(74, 158, 255, 0.35)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.05); }
  input { margin-top: 3px; accent-color: #4a9eff; }
`;

const RadioText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
`;

const RadioHint = styled.span`
  font-size: 11px;
  color: #777;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: ${p => p.$on ? 'rgba(168, 85, 247, 0.08)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${p => p.$on ? 'rgba(168, 85, 247, 0.35)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.05); }
  input { margin-top: 3px; accent-color: #a855f7; }
`;

const Counts = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  font-size: 12px;
`;

const Count = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  span:first-of-type { color: #666; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  span:last-of-type { color: #ddd; font-family: monospace; font-size: 14px; }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(74, 158, 255, 0.12);
  border: 1px solid rgba(74, 158, 255, 0.35);
  border-radius: 8px;
  color: #8ab4f8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  &:hover:not(:disabled) {
    background: rgba(74, 158, 255, 0.2);
    color: #fff;
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  svg { flex-shrink: 0; }
  svg.spinning { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Note = styled.p`
  margin: 0;
  font-size: 11px;
  color: #666;
  line-height: 1.5;
`;

// ─── Helpers ─────────────────────────────────────────────────────

function filenameStamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const CSV_COLS_BASE = [
  'timestamp', 'source', 'category', 'method', 'path', 'status_code', 'duration_ms',
  'summary', 'device_id', 'instance_id', 'ai_model',
  'tokens_input', 'tokens_output', 'estimated_cost_usd',
  'rag_results_count', 'error_message', 'actor',
];

// Text columns added when full=true. JSONB columns (rag_results, response_parsed,
// prompt_variables) are JSON-only — they'd be unreadable in Excel cells.
const CSV_COLS_FULL_EXTRA = [
  'system_prompt', 'user_prompt', 'response_final', 'response_text', 'request_body',
];

const EXCEL_CELL_LIMIT = 32000; // Excel chokes above ~32767

function csvEscape(v, truncate = false) {
  if (v == null) return '';
  let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  if (truncate && s.length > EXCEL_CELL_LIMIT) s = s.slice(0, EXCEL_CELL_LIMIT - 1) + '…';
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function entriesToCsv(entries, full) {
  const cols = full ? [...CSV_COLS_BASE, ...CSV_COLS_FULL_EXTRA] : CSV_COLS_BASE;
  const lines = [cols.join(',')];
  for (const e of entries) lines.push(cols.map(c => csvEscape(e[c], full)).join(','));
  // BOM ensures Excel reads UTF-8 (Finnish chars) correctly
  return '﻿' + lines.join('\r\n');
}

async function fetchAllMatching({ activeInstanceId, categoryFilter, deviceFilter, full, maxRows = Infinity }) {
  // Server caps full mode at 200/page (rows can be 50-200KB), slim mode at 500.
  const PAGE = full ? 200 : 500;
  const all = [];
  let offset = 0;
  let totalKnown = Infinity;
  while (offset < totalKnown && all.length < maxRows) {
    const remaining = maxRows - all.length;
    const lim = Math.min(PAGE, remaining);
    const url = new URL(API_ENDPOINTS.ACTION_LOG_UNIFIED());
    url.searchParams.set('limit', String(lim));
    url.searchParams.set('offset', String(offset));
    if (full) url.searchParams.set('full', 'true');
    if (activeInstanceId) url.searchParams.set('instanceId', activeInstanceId);
    if (categoryFilter) url.searchParams.set('category', categoryFilter);
    if (deviceFilter) url.searchParams.set('deviceId', deviceFilter);

    const res = await fetch(url.toString(), { credentials: 'include' });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    const data = await res.json();
    all.push(...data.entries);
    totalKnown = data.total ?? all.length;
    if (data.entries.length < lim) break;
    offset += lim;
  }
  return all;
}

// ─── Component ───────────────────────────────────────────────────

/**
 * ExportPanel — download current activity log view as JSON or CSV (Excel-compatible).
 *
 * Reads data via contextRef so registered drawer tab stays mounted while screen state changes.
 * contextRef.current shape: { entries, total, activeInstanceId, categoryFilter, deviceFilter }
 */
export default function ExportPanel({ contextRef }) {
  const { t } = useTranslation();
  const notice = useNotice();
  const [scope, setScope] = useState('view'); // 'view' | 'all'
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(null); // 'json' | 'csv' | null

  const ctx = contextRef.current;
  const { entries, total, activeInstanceId, categoryFilter, deviceFilter } = ctx;

  const hasFilters = !!(activeInstanceId || categoryFilter || deviceFilter);
  const exportCount = scope === 'all' ? total : entries.length;
  const disabled = exportCount === 0 || busy != null;

  const resolveEntries = async () => {
    // In slim mode, view scope can reuse already-loaded entries (no refetch).
    // Full mode always refetches because the listing only has slim columns.
    if (!full && scope === 'view') return entries;
    const filters = { activeInstanceId, categoryFilter, deviceFilter, full };
    if (scope === 'view') return fetchAllMatching({ ...filters, maxRows: entries.length });
    return fetchAllMatching(filters);
  };

  const handleJson = async () => {
    if (disabled) return;
    setBusy('json');
    try {
      const data = await resolveEntries();
      const payload = {
        exported_at: new Date().toISOString(),
        scope,
        full,
        filters: {
          instanceId: activeInstanceId,
          category: categoryFilter || null,
          deviceId: deviceFilter || null,
        },
        count: data.length,
        total_matching: total,
        entries: data,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
      triggerDownload(blob, `activity-log-${filenameStamp()}.json`);
    } catch (err) {
      console.error('[Export] JSON failed:', err);
      notice.error(t('activityLog.exportFailed', { error: err.message }));
    } finally {
      setBusy(null);
    }
  };

  const handleCsv = async () => {
    if (disabled) return;
    setBusy('csv');
    try {
      const data = await resolveEntries();
      const blob = new Blob([entriesToCsv(data, full)], { type: 'text/csv;charset=utf-8' });
      triggerDownload(blob, `activity-log-${filenameStamp()}.csv`);
    } catch (err) {
      console.error('[Export] CSV failed:', err);
      notice.error(t('activityLog.exportFailed', { error: err.message }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Body>
      <Section>
        <SectionTitle>Aktiiviset suodattimet</SectionTitle>
        {hasFilters ? (
          <FilterPills>
            {activeInstanceId && <Pill $mono>instance: {activeInstanceId.substring(0, 8)}…</Pill>}
            {categoryFilter && <Pill>kategoria: {categoryFilter}</Pill>}
            {deviceFilter && <Pill $mono>laite: {deviceFilter.substring(0, 8)}…</Pill>}
          </FilterPills>
        ) : (
          <FilterPills><PillMuted>Ei suodattimia — kaikki instanssit</PillMuted></FilterPills>
        )}
      </Section>

      <Section>
        <SectionTitle>Laajuus</SectionTitle>
        <RadioGroup>
          <RadioRow $selected={scope === 'view'}>
            <input type="radio" checked={scope === 'view'} onChange={() => setScope('view')} />
            <RadioText>
              <span>Ladattu näkymä</span>
              <RadioHint>Vain tällä hetkellä näkyvät rivit ({entries.length} kpl)</RadioHint>
            </RadioText>
          </RadioRow>
          <RadioRow $selected={scope === 'all'}>
            <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />
            <RadioText>
              <span>Kaikki suodatusta vastaavat</span>
              <RadioHint>Hakee kaikki {total} riviä palvelimelta ennen latausta</RadioHint>
            </RadioText>
          </RadioRow>
        </RadioGroup>
      </Section>

      <Section>
        <SectionTitle>Sisältö</SectionTitle>
        <CheckboxRow $on={full}>
          <input type="checkbox" checked={full} onChange={e => setFull(e.target.checked)} />
          <RadioText>
            <span>Sisällytä koko sisältö</span>
            <RadioHint>
              Täydet promptit, RAG-osumat, vastaukset ja pyyntörungot. JSON sisältää kaiken;
              Excelin solut katkaistaan {EXCEL_CELL_LIMIT.toLocaleString('fi-FI')} merkkiin (rikkaat JSONB-kentät kuten rag_results vain JSON-viennissä).
              Suuret rivit (50–200&nbsp;KB) — palvelin sivuttaa 200 kerrallaan.
            </RadioHint>
          </RadioText>
        </CheckboxRow>
      </Section>

      <Counts>
        <Count><span>Ladataan</span><span>{exportCount}</span></Count>
        <Count><span>Suodatusta vastaa</span><span>{total}</span></Count>
        <Count><span>Näkymässä</span><span>{entries.length}</span></Count>
      </Counts>

      <Section>
        <SectionTitle>Muoto</SectionTitle>
        <Buttons>
          <ActionButton onClick={handleJson} disabled={disabled}>
            {busy === 'json'
              ? <Loader2 size={18} className="spinning" />
              : <FileJson size={18} />}
            <div>
              <div>Lataa JSON</div>
              <Note style={{ marginTop: 2 }}>
                {full
                  ? 'Sisältää koko AI-datan: promptit, RAG-osumat, vastaukset'
                  : 'Yhteenveto-metadata kehittäjille / työkaluille'}
              </Note>
            </div>
          </ActionButton>
          <ActionButton onClick={handleCsv} disabled={disabled}>
            {busy === 'csv'
              ? <Loader2 size={18} className="spinning" />
              : <FileSpreadsheet size={18} />}
            <div>
              <div>Lataa Excel</div>
              <Note style={{ marginTop: 2 }}>
                {full
                  ? 'CSV (UTF-8 BOM) — promptit ja vastaukset mukana, soluraja 32K'
                  : 'CSV (UTF-8 BOM) — avautuu suoraan Exceliin'}
              </Note>
            </div>
          </ActionButton>
        </Buttons>
      </Section>

      <Note>
        Tiedostonimi: <code>activity-log-{filenameStamp()}.{`{json|csv}`}</code>
      </Note>
    </Body>
  );
}
